import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Anchor, Box, Button, createStyles, Drawer, Indicator} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import DOMPurify from 'dompurify';
import React, {useCallback, useEffect, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {navigate} from 'BenchlingUtil';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import EntityStore from 'stores/EntityStore';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {CenteredSpinner} from 'ui-library/Spinner';

const useButtonStyles = createStyles((theme) => ({
  button: {
    color: theme.colors.gray[8],
    borderColor: theme.other.border.defaultColor,
  },
}));

const useDrawerStyles = createStyles((theme) => ({
  drawerHeader: {
    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
    borderBottom: `1px solid ${theme.other.border.defaultColor}`,
    marginBottom: theme.spacing.md,
  },
  drawerTitle: {
    fontSize: 20,
    fontWeight: theme.other.fontWeights.bold,
  },
  drawerBody: {
    padding: theme.spacing.xl,

    '& img': {
      height: 'auto',
      maxWidth: '100%',
    },
  },
}));

/** @package Exported for testing. */
export function getHomepageWhatsNewArticleUrl(homepageWhatsNewZendeskHelpArticleId: string): string {
  return `https://help.benchling.com/api/v2/help_center/articles/${homepageWhatsNewZendeskHelpArticleId}`;
}

function WhatsNewDrawer({
  open,
  onClose,
  homepageWhatsNewZendeskHelpArticleId,
}: {
  open: boolean;
  onClose: () => void;
  homepageWhatsNewZendeskHelpArticleId: string;
}): JSX.Element {
  const [whatsNew, setWhatsNew] = useState<string>();
  const {classes} = useDrawerStyles();

  useEffect(() => {
    if (homepageWhatsNewZendeskHelpArticleId && open && !whatsNew) {
      fetch(getHomepageWhatsNewArticleUrl(homepageWhatsNewZendeskHelpArticleId))
        .then((response) => response.json())
        .then(({article: {body}}) => {
          setWhatsNew(DOMPurify.sanitize(body));
        });
    }
  }, [homepageWhatsNewZendeskHelpArticleId, open, whatsNew]);

  return (
    <Drawer
      classNames={{body: classes.drawerBody, header: classes.drawerHeader, title: classes.drawerTitle}}
      opened={open}
      onClose={onClose}
      title="What's New"
      position="right"
      size="650px"
      overlayProps={{opacity: 0.2}}
    >
      {whatsNew ? (
        <>
          {!BenchlingConfig.NAV_ENABLE_HOMEPAGE_WHATS_NEW && (
            <Alert icon={<BenchlingIcon icon="info-circle-outline" />} fz="md" mb="md">
              By default, this is only available to Tenant Admins, but if you'd like to enable tenant-wide
              access you can turn it on in Settings{' '}
              <Anchor
                href="/admin/settings"
                onClick={(e) => {
                  navigate(e, '/admin/settings');
                }}
              >
                here
              </Anchor>
              .
            </Alert>
          )}
          {/* eslint-disable-next-line react/no-danger */}
          <Box fz="md" dangerouslySetInnerHTML={{__html: whatsNew}} />
        </>
      ) : (
        <CenteredSpinner />
      )}
    </Drawer>
  );
}

interface WhatsNewButtonProps {
  homepageWhatsNewZendeskHelpArticleId: string;
  userId: string;
}

function WhatsNewButton({homepageWhatsNewZendeskHelpArticleId, userId}: WhatsNewButtonProps): JSX.Element {
  const [opened, {open, close}] = useDisclosure(false);
  const homepageLastSeenWhatsNewKey = `${userId}:homepageLastSeenWhatsNew`;
  const [lastSeenWhatsNew, setLastSeenWhatsNew] = useState<string | null>(() =>
    localStorage.getItem(homepageLastSeenWhatsNewKey)
  );
  const {classes} = useButtonStyles();

  const hasSeenLatestWhatsNew = lastSeenWhatsNew === homepageWhatsNewZendeskHelpArticleId;
  const whatsNewButtonLabel = hasSeenLatestWhatsNew ? "What's new" : "What's new (unread)";

  const handleOpen = (): void => {
    localStorage.setItem(homepageLastSeenWhatsNewKey, homepageWhatsNewZendeskHelpArticleId);
    setLastSeenWhatsNew(homepageWhatsNewZendeskHelpArticleId);
    open();
  };

  return (
    <>
      <Indicator disabled={hasSeenLatestWhatsNew} color="green" size={15} offset={2}>
        <Button
          size="md"
          radius="md"
          variant="secondary"
          onClick={handleOpen}
          className={classes.button}
          aria-label={whatsNewButtonLabel}
          {...getTestProps({component: 'WhatsNewButton', element: 'button'})}
        >
          What's new
        </Button>
      </Indicator>
      <WhatsNewDrawer
        open={opened}
        onClose={close}
        homepageWhatsNewZendeskHelpArticleId={homepageWhatsNewZendeskHelpArticleId}
      />
    </>
  );
}

export default function WhatsNewButtonWrapper(): JSX.Element | null {
  const {homepageWhatsNewZendeskHelpArticleId} = useRolloutFlags();
  const user = useStoreSelector<typeof EntityStore, EntityStoreUser | null>(
    EntityStore,
    useCallback((store) => store.currentUser(), [])
  );

  if (
    !homepageWhatsNewZendeskHelpArticleId ||
    !user ||
    !(user.isTenantAdmin || BenchlingConfig.NAV_ENABLE_HOMEPAGE_WHATS_NEW)
  ) {
    return null;
  }

  return (
    <WhatsNewButton
      key={`${user.id}:${homepageWhatsNewZendeskHelpArticleId}`}
      userId={user.id}
      homepageWhatsNewZendeskHelpArticleId={homepageWhatsNewZendeskHelpArticleId}
    />
  );
}
